import React, { useState } from 'react';
import { Button, Checkbox, MessageBarBody, Spinner } from '@fluentui/react-components';
import { ArrowUndo16Regular, Checkmark16Regular, ChevronDown16Regular, ChevronUp16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ContractReview, ContractReviewFinding, FindingDecision, ReviewEvidence } from '../../../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../../../models/HttpError';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import {
  canDecide,
  canUndoFinding,
  ChangeLine,
  cleanQuote,
  describeChange,
  describeSuggested,
  errorText,
  findingKind,
  formatDateTime,
  formatFieldValue,
  formatFieldValuePretty,
  formatJsonValue,
  hasApplyProblem,
  isAutoDecided,
  shortFileName,
  stripTechnicalRefs,
  truncateText,
  workStateOf
} from '../ContractReview.Functions';
import { useCodeText, useFieldLabel, useHumanizeText, useValueLabel } from '../ContractReview.Text';
import { ApplyResultPill, AutoDecidedPill, DecisionPill, Pill } from '../ContractReviewPills/ContractReviewPills.Component';
import { StatusTone } from '../../../../theme/guideTokens';

/*
 * Tarjeta de un cambio o aviso, pensada para alguien que no conoce la herramienta:
 *   qué encontró (título en lenguaje llano), sobre qué (concepto, etapa…), qué cambiaría (hoy → según el documento),
 *   dónde lo dice el documento (cita), y dos botones con palabras: Aceptar / Rechazar.
 * Tres clases de ítem, dichas con palabras: se aplica solo, hay que cargarlo a mano, o es un aviso.
 * Lo técnico (confianza, origen, valores crudos, campos del parche) queda plegado en "Más detalles".
 */

export interface ContractReviewItemCardProps {
  review: Pick<ContractReview, 'status' | 'mode'>;
  finding: ContractReviewFinding;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  deciding?: boolean;
  decisionError?: ErrorType;
  /** Pide la decisión: la tarjeta no la guarda, la pantalla abre el diálogo de comentario. */
  onDecide?: (decision: FindingDecision) => void;
  /** Contracts 021 §4.1: pide deshacer un cambio aplicado (la pantalla confirma). */
  onUndo?: () => void;
  undoing?: boolean;
  undoError?: ErrorType;
  defaultExpanded?: boolean;
}

const SEVERITY_TONE: Record<string, StatusTone> = { High: 'danger', Medium: 'warning', Low: 'info', Info: 'neutral' };

export const ContractReviewItemCard: React.FC<ContractReviewItemCardProps> = ({
  review,
  finding,
  selectable,
  selected,
  onSelect,
  deciding,
  decisionError,
  onDecide,
  onUndo,
  undoing,
  undoError,
  defaultExpanded
}) => {
  const { t } = useTranslation();
  const codeText = useCodeText();
  const fieldLabel = useFieldLabel();
  const valueLabel = useValueLabel();
  const humanize = useHumanizeText();
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  const state = workStateOf(finding);
  const kind = findingKind(finding);
  const isChange = kind !== 'observation';
  const allowed = canDecide(review, finding);
  const undoable = !!onUndo && canUndoFinding(review, finding);
  const busy = !!deciding || !!undoing;
  const changes = kind === 'change' || finding.patch ? describeChange(finding) : describeSuggested(finding);
  const hasCurrent = changes.some(line => line.current !== undefined);
  const firstQuote = finding.evidence[0];
  const importanceKey = `contractReview.item.importance.${finding.severity}`;
  const importanceText = t(importanceKey);
  const description = finding.description ? humanize(stripTechnicalRefs(finding.description)) : null;
  const valuesText = (values: Record<string, unknown>) =>
    Object.entries(values)
      .map(([field, value]) => `${fieldLabel(field)}: ${valueLabel(value)}`)
      .join(' · ');

  const evidenceSource = (e: ReviewEvidence) =>
    e.fileName && e.page !== null
      ? t('contractReview.item.evidenceSource', { file: shortFileName(e.fileName), page: e.page })
      : e.fileName
        ? t('contractReview.item.evidenceFile', { file: shortFileName(e.fileName) })
        : e.page !== null
          ? t('contractReview.item.evidencePage', { page: e.page })
          : undefined;

  const quote = (e: ReviewEvidence, index: number, short: boolean) => {
    const source = evidenceSource(e);
    const cleaned = cleanQuote(e.quote);
    return (
      <figure key={index} className="crv-quote">
        {cleaned.table && <span className="crv-quote__kind">{t('contractReview.item.quoteRow')}</span>}
        <blockquote className="crv-quote__text" title={short && cleaned.text.length > 160 ? cleaned.text : undefined}>
          “{short ? truncateText(cleaned.text) : cleaned.text}”
        </blockquote>
        {source && (
          <figcaption className="crv-quote__source" title={e.fileName ?? undefined}>
            {source}
          </figcaption>
        )}
      </figure>
    );
  };

  const lineValue = (line: ChangeLine, side: 'current' | 'next', fallback: string) => {
    if (side === 'next' && line.ref) return t('contractReview.item.refPending', { label: line.ref.label });
    if (side === 'current') return line.current === undefined ? fallback : valueLabel(line.rawCurrent);
    return valueLabel(line.rawNext);
  };
  const lines = (side: 'current' | 'next', fallback: string) => (
    <dl className="crv-change__lines">
      {changes.map(line => (
        <div key={line.field} className="crv-change__line">
          <dt>{fieldLabel(line.field)}</dt>
          <dd>
            <span className="crv-change__value">{lineValue(line, side, fallback)}</span>
          </dd>
        </div>
      ))}
    </dl>
  );

  const singleBox = (label: string) => (
    <div className="crv-change crv-change--single" data-testid={`finding-${finding.id}-change`}>
      <div className="crv-change__box crv-change__box--to">
        <span className="crv-change__label">{label}</span>
        {lines('next', '—')}
      </div>
    </div>
  );

  const changePreview = () => {
    if (changes.length === 0) return null;
    const op = finding.patch?.op;
    if (kind === 'manual') return singleBox(t('contractReview.item.proposed'));
    if (op === 'Delete') return singleBox(t('contractReview.item.deletes'));
    if (op === 'Create') return singleBox(t('contractReview.item.creates'));
    if (!hasCurrent) return singleBox(t('contractReview.item.document'));
    return (
      <div className="crv-change" data-testid={`finding-${finding.id}-change`}>
        <div className="crv-change__box crv-change__box--from">
          <span className="crv-change__label">{t('contractReview.item.today')}</span>
          {lines('current', t('contractReview.item.noCurrent'))}
        </div>
        <span className="crv-change__arrow" aria-hidden="true">
          →
        </span>
        <div className="crv-change__box crv-change__box--to">
          <span className="crv-change__label">{t('contractReview.item.document')}</span>
          {lines('next', '—')}
        </div>
      </div>
    );
  };

  const decisionButton = (decision: 'Accepted' | 'Rejected') => {
    const chosen = finding.decision === decision;
    const label =
      decision === 'Accepted'
        ? isChange
          ? t('contractReview.decisionActions.accept')
          : t('contractReview.decisionActions.acceptObservation')
        : isChange
          ? t('contractReview.decisionActions.reject')
          : t('contractReview.decisionActions.rejectObservation');
    return (
      <Button
        appearance={chosen ? 'primary' : decision === 'Accepted' && state === 'pending' ? 'primary' : 'secondary'}
        icon={decision === 'Accepted' ? <Checkmark16Regular /> : <Dismiss16Regular />}
        aria-pressed={chosen}
        disabled={!allowed || chosen || busy}
        onClick={() => onDecide?.(decision)}
        data-testid={`finding-${finding.id}-${decision.toLowerCase()}`}
      >
        {label}
      </Button>
    );
  };

  const decidedText = isAutoDecided(finding)
    ? t('contractReview.autoApply.decidedByIA', { date: formatDateTime(finding.decidedAt) })
    : finding.decision === 'Accepted' && finding.decidedBy
      ? t('contractReview.item.acceptedBy', { user: finding.decidedBy, date: formatDateTime(finding.decidedAt) })
      : finding.decision === 'Rejected' && finding.decidedBy
        ? t('contractReview.item.rejectedBy', { user: finding.decidedBy, date: formatDateTime(finding.decidedAt) })
        : undefined;

  const question = kind === 'change' ? t('contractReview.item.question.change') : kind === 'manual' ? t('contractReview.item.question.manual') : t('contractReview.item.question.observation');

  return (
    <article
      className={`crv-item crv-item--${state}${selected ? ' crv-item--selected' : ''}`}
      aria-labelledby={`finding-title-${finding.id}`}
      data-testid={`finding-${finding.id}`}
      data-state={state}
      data-kind={kind}
    >
      <div className="crv-item__main">
        {selectable && (
          <div className="crv-item__check">
            <Checkbox checked={!!selected} disabled={!allowed} aria-label={t('contractReview.item.select', { title: finding.title })} onChange={(_, data) => onSelect?.(!!data.checked)} />
          </div>
        )}

        <div className="crv-item__body">
          <div className="crv-item__head">
            <div className="crv-item__tags">
              <Pill tone={SEVERITY_TONE[finding.severity] ?? 'neutral'} text={importanceText === importanceKey ? finding.severity : importanceText} testId={`importance-${finding.severity}`} />
              <span className="crv-item__kind" data-testid={`finding-${finding.id}-kind`}>
                {t(`contractReview.item.kind.${kind}`)} · {codeText('category', String(finding.category))}
              </span>
            </div>
            <div className="crv-item__state">
              {state === 'applied' ? <ApplyResultPill status="Applied" /> : <DecisionPill decision={finding.decision} />}
              {state !== 'applied' && finding.applyStatus && <ApplyResultPill status={finding.applyStatus} />}
              {isAutoDecided(finding) && finding.decision === 'Accepted' && <AutoDecidedPill />}
            </div>
          </div>

          <h3 className="crv-item__title" id={`finding-title-${finding.id}`}>
            {finding.title}
          </h3>
          {finding.target && (
            <p className="crv-item__about">{t('contractReview.item.about', { type: codeText('entityType', finding.target.entityType), label: finding.target.label })}</p>
          )}
          {description && <p className="crv-item__desc">{description}</p>}

          {changePreview()}
          {kind === 'manual' && (
            <p className="crv-item__hint" data-testid={`finding-${finding.id}-manual`}>
              {t('contractReview.item.manualHint')}
            </p>
          )}
          {kind === 'manual' && finding.patch && !finding.patchValid && finding.patchInvalidReason && (
            <p className="crv-item__warn" data-testid={`finding-${finding.id}-invalid`}>
              {t('contractReview.item.manualReason', { reason: humanize(stripTechnicalRefs(finding.patchInvalidReason)) })}
            </p>
          )}
          {kind === 'observation' && <p className="crv-item__hint">{t('contractReview.item.observationHint')}</p>}

          {firstQuote ? (
            <div className="crv-item__evidence">
              <span className="crv-item__evidence-label">{t('contractReview.item.evidenceLabel')}</span>
              {quote(firstQuote, 0, true)}
            </div>
          ) : (
            <p className="crv-item__muted">{t('contractReview.item.noEvidence')}</p>
          )}

          {hasApplyProblem(finding) && (
            <GuideMessageBar intent="warning" layout="multiline" data-testid={`finding-${finding.id}-apply-problem`}>
              <MessageBarBody>
                {t(`contractReview.item.applyProblem.${finding.applyStatus}`, { message: humanize(finding.applyMessage ?? '—') })}
                {finding.applyStatus === 'Conflict' && finding.applyMessage ? ` ${humanize(finding.applyMessage)}.` : ''}
                {finding.currentValues ? ` ${t('contractReview.item.applyProblemCurrent', { values: valuesText(finding.currentValues) })}` : ''}
                {allowed ? ` ${t('contractReview.item.retryHint')}` : ''}
              </MessageBarBody>
            </GuideMessageBar>
          )}
          {finding.applyStatus === 'Undone' && finding.undoneAt && (
            <p className="crv-item__muted" data-testid={`finding-${finding.id}-undone`}>
              {t('contractReview.undo.undoneBy', { user: finding.undoneBy ?? '—', date: formatDateTime(finding.undoneAt) })}
              {finding.undoneValues ? ` · ${t('contractReview.undo.restored', { values: valuesText(finding.undoneValues) })}` : ''}
            </p>
          )}

          <Button
            appearance="transparent"
            size="small"
            className="crv-item__more"
            icon={expanded ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
            aria-expanded={expanded}
            aria-controls={`finding-details-${finding.id}`}
            onClick={() => setExpanded(v => !v)}
            data-testid={`finding-${finding.id}-toggle`}
          >
            {expanded ? t('contractReview.item.lessDetails') : t('contractReview.item.moreDetails')}
          </Button>

          {expanded && (
            <div className="crv-item__details" id={`finding-details-${finding.id}`} data-testid={`finding-${finding.id}-detail`}>
              {finding.evidence.length > 0 && (
                <div className="crv-item__evidence">
                  <span className="crv-item__evidence-label">{t('contractReview.item.evidenceLabel')}</span>
                  {finding.evidence.map((e, index) => quote(e, index, false))}
                </div>
              )}
              <p className="crv-item__muted">
                {finding.confidence !== null && <span>{t('contractReview.item.confidence', { value: Math.round(finding.confidence * 100) })}</span>}
                {finding.confidence !== null && finding.origin && <span className="crv-sep"> · </span>}
                {finding.origin && <span>{t('contractReview.item.origin', { value: codeText('origin', finding.origin) })}</span>}
              </p>
              {(finding.currentValue !== null || finding.suggestedValue !== null || finding.patch) && (
                <details className="crv-item__technical">
                  <summary>{t('contractReview.item.technical')}</summary>
                  {(finding.currentValue !== null || finding.suggestedValue !== null) && (
                    <div className="crv-item__raw">
                      <div>
                        <span className="crv-item__evidence-label">{t('contractReview.item.currentRaw')}</span>
                        <pre data-testid="current-value">{formatJsonValue(finding.currentValue)}</pre>
                      </div>
                      <div>
                        <span className="crv-item__evidence-label">{t('contractReview.item.suggestedRaw')}</span>
                        <pre data-testid="suggested-value">{formatJsonValue(finding.suggestedValue)}</pre>
                      </div>
                    </div>
                  )}
                  {finding.patch && (
                    <div>
                      <span className="crv-item__evidence-label">
                        {t('contractReview.item.patchFields')} · {codeText('patchOp', finding.patch.op)} · {codeText('entityType', finding.patch.entityType)}
                        {finding.patch.appliesTo ? ` · ${codeText('appliesTo', finding.patch.appliesTo)}` : ''}
                      </span>
                      <dl className="crv-change__lines">
                        {Object.entries(finding.patch.fields).map(([field, v]) => (
                          <div key={field} className="crv-change__line">
                            <dt>
                              {fieldLabel(field)} <code>{field}</code>
                            </dt>
                            <dd>{typeof v === 'object' && v !== null ? <pre>{formatFieldValuePretty(v)}</pre> : formatFieldValue(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="crv-item__foot">
        <div className="crv-item__status">
          {state === 'applied' && finding.appliedAt && !isAutoDecided(finding) && (
            <span>{t('contractReview.item.appliedAt', { date: formatDateTime(finding.appliedAt), user: finding.appliedBy ?? '—' })}</span>
          )}
          {decidedText && <span>{decidedText}</span>}
          {finding.decisionComment && <span className="crv-item__comment">{t('contractReview.item.comment', { text: finding.decisionComment })}</span>}
          {finding.undoMessage && finding.applyStatus === 'Applied' && (
            <span data-testid={`finding-${finding.id}-undo-message`}>{t('contractReview.undo.lastAttempt', { message: humanize(finding.undoMessage) })}</span>
          )}
          {state === 'pending' && allowed && onDecide && <span className="crv-item__question">{question}</span>}
        </div>
        <div className="crv-item__actions">
          {busy && <Spinner size="extra-tiny" />}
          {undoable && (
            <Button appearance="secondary" icon={<ArrowUndo16Regular />} disabled={busy} onClick={onUndo} data-testid={`finding-${finding.id}-undo`}>
              {t('contractReview.undo.finding')}
            </Button>
          )}
          {onDecide && allowed && (
            <>
              {decisionButton('Accepted')}
              {decisionButton('Rejected')}
              {state !== 'pending' && (
                <Button appearance="transparent" size="small" disabled={busy} onClick={() => onDecide('Pending')} data-testid={`finding-${finding.id}-pending`}>
                  {t('contractReview.decisionActions.reset')}
                </Button>
              )}
            </>
          )}
        </div>
      </footer>

      {decisionError && (
        <GuideMessageBar intent="error" role="alert" layout="multiline">
          <MessageBarBody>{t('contractReview.decisionActions.error', { message: errorText(decisionError) ?? t('contractReview.errors.generic') })}</MessageBarBody>
        </GuideMessageBar>
      )}
      {undoError && (
        <GuideMessageBar intent="error" role="alert" layout="multiline" data-testid={`finding-${finding.id}-undo-error`}>
          <MessageBarBody>{t('contractReview.undo.findingError', { message: errorText(undoError) ?? t('contractReview.errors.generic') })}</MessageBarBody>
        </GuideMessageBar>
      )}
    </article>
  );
};

export default ContractReviewItemCard;
